import { apiRequest } from '../lib/api-client';

/**
 * The control plane (context.md sections 11-12, 92-100).
 *
 * A different plane, not a different flavour of the tenant API: these endpoints live under `/admin`,
 * require `PLATFORM_ADMIN`, and nothing here is reachable with a tenant session. Kept in its own
 * module so no tenant screen can import one by accident.
 */

export interface TenantRow {
  id: string;
  name: string;
  slug: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'READ_ONLY' | 'DELETED';
  plan_key: string | null;
  subscription_status: string | null;
  trial_ends_at: string | null;
  user_count: number;
  created_at: string;
}

export function listTenants(filters: { search?: string; status?: string } = {}): Promise<{
  tenants: TenantRow[];
  next_cursor: string | null;
}> {
  const params = new URLSearchParams({ limit: '100' });

  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }

  return apiRequest(`/admin/tenants?${params.toString()}`);
}

export interface PlanRow {
  key: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  billing_period: string;
  trial_days: number;
  is_public: boolean;
  is_default: boolean;
  active: boolean;
  features: string[];
  limits: Record<string, number>;
}

export function listPlans(): Promise<{ plans: PlanRow[] }> {
  return apiRequest('/admin/plans');
}

export interface FeatureRow {
  key: string;
  name: string;
  description: string;
  /** The plans that carry it. Empty means none does. */
  plans: string[];
  /**
   * True when no plan includes it.
   *
   * These are the experimental ones, gated on purpose — and the reason this flag is served rather
   * than inferred on the screen: without it somebody upgrades a customer to the most expensive plan
   * trying to reach one, which cannot work and says nothing about why.
   */
  only_by_override: boolean;
}

export function listFeatures(): Promise<{ features: FeatureRow[] }> {
  return apiRequest('/admin/features');
}

// ---------------------------------------------------------------------------
// One tenant, and the things that can be done to it

export interface Entitlements {
  tenantId: string;
  planKey: string;
  planName: string;
  status: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  graceEndsAt: string | null;
  features: string[];
  limits: Record<string, number>;
  readOnly: boolean;
}

export interface OverrideRow {
  id: string;
  kind: 'FEATURE' | 'LIMIT';
  target_key: string;
  value: unknown;
  reason: string;
  ends_at: string | null;
  created_at: string;
}

export interface SubscriptionEvent {
  id: string;
  type: string;
  detail: unknown;
  occurred_at: string;
}

export interface TenantDetail extends TenantRow {
  entitlements: Entitlements;
  overrides: OverrideRow[];
  subscription_events: SubscriptionEvent[];
}

export function getTenant(id: string): Promise<TenantDetail> {
  return apiRequest(`/admin/tenants/${id}`);
}

/**
 * Every write below carries a reason.
 *
 * Not a formality: these are actions a customer feels, taken by somebody who is not the customer,
 * and the reason is what the audit trail has to show afterwards (context.md section 17). The API
 * refuses a reason shorter than three characters; the interface refuses an empty one before that.
 */
export function setTenantStatus(
  id: string,
  input: { status: 'ACTIVE' | 'SUSPENDED' | 'READ_ONLY'; reason: string },
): Promise<TenantRow> {
  return apiRequest(`/admin/tenants/${id}/status`, { method: 'PATCH', body: input });
}

export function changePlan(
  id: string,
  input: { planKey: string; reason: string },
): Promise<unknown> {
  return apiRequest(`/admin/tenants/${id}/plan`, { method: 'PATCH', body: input });
}

export function setSubscriptionStatus(
  id: string,
  input: { status: string; reason: string },
): Promise<unknown> {
  return apiRequest(`/admin/tenants/${id}/subscription/status`, { method: 'PATCH', body: input });
}

export function extendTrial(id: string, input: { days: number; reason: string }): Promise<unknown> {
  return apiRequest(`/admin/tenants/${id}/subscription/extend-trial`, {
    method: 'POST',
    body: input,
  });
}

export function overrideFeature(
  id: string,
  input: { featureKey: string; enabled: boolean; reason: string; endsAt?: string },
): Promise<OverrideRow> {
  return apiRequest(`/admin/tenants/${id}/overrides/features`, { method: 'POST', body: input });
}

export function overrideLimit(
  id: string,
  input: { limitKey: string; value: number | null; reason: string; endsAt?: string },
): Promise<OverrideRow> {
  return apiRequest(`/admin/tenants/${id}/overrides/limits`, { method: 'POST', body: input });
}

export function revokeOverride(tenantId: string, overrideId: string): Promise<void> {
  return apiRequest(`/admin/tenants/${tenantId}/overrides/${overrideId}`, { method: 'DELETE' });
}

export interface SupportSession {
  id: string;
  token?: string;
  reason: string;
  read_only: boolean;
  expires_at: string;
  ended_at: string | null;
  created_at: string;
}

export function startSupportSession(
  tenantId: string,
  input: { reason: string; readOnly: boolean; durationMinutes: number },
): Promise<SupportSession> {
  return apiRequest(`/admin/tenants/${tenantId}/support-sessions`, {
    method: 'POST',
    body: input,
  });
}

export function listSupportSessions(tenantId: string): Promise<{ sessions: SupportSession[] }> {
  return apiRequest(`/admin/tenants/${tenantId}/support-sessions`);
}

export function endSupportSession(sessionId: string): Promise<void> {
  return apiRequest(`/admin/support-sessions/${sessionId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Invoices, from the control plane

export interface AdminInvoice {
  id: string;
  tenant_id: string;
  number: number;
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE';
  plan_key: string;
  amount_cents: number;
  currency: string;
  period_start: string;
  period_end: string;
  due_at: string;
  paid_at: string | null;
  payment_method: string | null;
  payment_reference: string | null;
}

export function listAdminInvoices(filters: { tenantId?: string; status?: string } = {}): Promise<{
  invoices: AdminInvoice[];
}> {
  const params = new URLSearchParams({ limit: '100' });

  if (filters.tenantId) params.set('tenantId', filters.tenantId);
  if (filters.status) params.set('status', filters.status);

  return apiRequest(`/admin/billing/invoices?${params.toString()}`);
}

export function confirmPayment(
  id: string,
  input: { method: string; reference?: string | null; reason: string },
): Promise<AdminInvoice> {
  return apiRequest(`/admin/billing/invoices/${id}/pay`, { method: 'POST', body: input });
}

export function voidInvoice(id: string, reason: string): Promise<AdminInvoice> {
  return apiRequest(`/admin/billing/invoices/${id}/void`, { method: 'POST', body: { reason } });
}

// ---------------------------------------------------------------------------
// Safe Cortex's own numbers

export interface SaasMetrics {
  asOf: string;
  months: number;
  revenue: {
    mrrCents: number;
    arrCents: number;
    arpuCents: number | null;
    payingAccounts: number;
  };
  retention: {
    grossRetention: number | null;
    netRetention: number | null;
    logoChurn: number | null;
    revenueChurn: number | null;
    startingCents: number;
    churnedCents: number;
    churnedAccounts: number;
    startingAccounts: number;
    /** Null until a month has been snapshotted: "not measured yet", not "zero". */
    expansionCents: number | null;
    contractionCents: number | null;
    newCents: number | null;
    measuredFrom: string | null;
  };
  trials: { ended: number; converted: number; conversion: number | null };
  lifetimeValueCents: number | null;
  funnel: { status: string; total: number }[];
  plans: { key: string; name: string; accounts: number; mrrCents: number }[];
  history: {
    month: string;
    invoicedCents: number;
    paidCents: number;
    invoices: number;
    paidInvoices: number;
    newTenants: number;
    churnedAccounts: number;
  }[];
  usage: Record<string, number>;
}

export function saasMetrics(months = 12): Promise<SaasMetrics> {
  return apiRequest(`/admin/analytics/saas?months=${months}`);
}

/** A rate as a percentage, or a dash when there was nothing to compute it from. */
export function percent(value: number | null): string {
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Product analytics (Phase 32)
// ---------------------------------------------------------------------------

export type HealthBand = 'NEW' | 'THRIVING' | 'HEALTHY' | 'QUIET' | 'DORMANT';

export interface ProductOverview {
  windowDays: number;
  tenants: number;
  activeTenants: number;
  activation: {
    activated: number;
    share: number | null;
    funnel: { milestone: string; tenants: number; share: number | null }[];
  };
  adoption: { area: string; tenants: number; share: number | null }[];
  bands: Record<HealthBand, number>;
  cohorts: { month: string; size: number; retained: (number | null)[] }[];
  list: {
    id: string;
    name: string;
    planKey: string | null;
    createdAt: string;
    members: number;
    activeUsers: number;
    activeDays: number;
    areas: number;
    lastEventAt: string | null;
    score: number;
    band: HealthBand;
    activated: boolean;
    milestones: string[];
  }[];
}

export function productOverview(months = 6): Promise<ProductOverview> {
  return apiRequest(`/admin/analytics/product?months=${months}`);
}

export interface TenantPulse {
  health: {
    score: number;
    band: HealthBand;
    parts: { activity: number; breadth: number; people: number };
  };
  activation: {
    reached: string[];
    missing: string[];
    activated: boolean;
    activatedAt: string | null;
  };
  activeUsers: number;
  members: number;
  activeDays: number;
  events: number;
  lastEventAt: string | null;
  byArea: { area: string; events: number; users: number }[];
  users: { id: string; name: string; events: number; lastActiveAt: string | null }[];
}

export function tenantPulse(id: string): Promise<TenantPulse> {
  return apiRequest(`/admin/analytics/product/tenants/${id}`);
}
